import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, Clock, Instagram } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 12: TARANG - Festival Wave
 * Style: Festive, vibrant, celebratory with saffron/orange energy
 * Best for: Indian festive fashion, celebration wear, colorful ethnic collections
 * White background with saffron/orange accents
 */
const Template12 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [hoveredCollection, setHoveredCollection] = useState(null);
    const touchStartX = useRef(0);
    const videoRef = useRef(null);

    // Festival countdown
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    useEffect(() => {
        const getNextFestival = () => {
            const now = new Date();
            const year = now.getFullYear();
            const festivals = [
                { name: 'Navratri', date: new Date(year, 9, 2) },
                { name: 'Diwali', date: new Date(year, 10, 1) },
                { name: 'Holi', date: new Date(year + 1, 2, 14) },
            ];
            for (const f of festivals) {
                if (f.date > now) return f.date;
            }
            return festivals[0].date;
        };

        const target = getNextFestival();
        const timer = setInterval(() => {
            const now = new Date();
            const diff = target - now;
            if (diff <= 0) return;
            setCountdown({
                days: Math.floor(diff / (1000 * 60 * 60 * 24)),
                hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
                minutes: Math.floor((diff / (1000 * 60)) % 60),
                seconds: Math.floor((diff / 1000) % 60),
            });
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Festival Collections
    const collections = [
        {
            name: 'Diwali Collection',
            description: 'Illuminate the night with shimmering silhouettes and golden embroidery.',
            image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=600',
            color: '#EA580C',
        },
        {
            name: 'Navratri Styles',
            description: 'Nine nights, nine colors — vibrant garba-ready ensembles.',
            image: 'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=600',
            color: '#DC2626',
        },
        {
            name: 'Wedding Season',
            description: 'Bridal couture and celebration wear for the grandest occasions.',
            image: 'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=600',
            color: '#D97706',
        },
        {
            name: 'Holi Specials',
            description: 'Colors of joy — playful, lightweight, and celebration-ready.',
            image: 'https://images.pexels.com/photos/2118563/pexels-photo-2118563.jpeg?auto=compress&cs=tinysrgb&w=600',
            color: '#7C3AED',
        },
    ];

    // Products
    const products = [
        { id: 1, name: 'Saffron Silk Lehenga', price: '\u20B912,999', badge: 'FESTIVE', image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Zari Work Saree', price: '\u20B98,499', badge: 'NEW', image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Embroidered Anarkali', price: '\u20B96,999', badge: 'FESTIVE', image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Mirror Work Chaniya', price: '\u20B919,999', badge: 'PREMIUM', image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Bandhani Dupatta Set', price: '\u20B94,999', badge: 'FESTIVE', image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Festive Kurta Palazzo', price: '\u20B93,499', image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Bridal Red Lehenga', price: '\u20B915,999', badge: 'BRIDAL', image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Rangoli Print Saree', price: '\u20B91,499', image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    // Instagram Grid Images
    const instagramImages = [
        'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=400',
        'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400',
        'https://images.pexels.com/photos/2220316/pexels-photo-2220316.jpeg?auto=compress&cs=tinysrgb&w=400',
        'https://images.pexels.com/photos/2955819/pexels-photo-2955819.jpeg?auto=compress&cs=tinysrgb&w=400',
        'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400',
        'https://images.pexels.com/photos/2118563/pexels-photo-2118563.jpeg?auto=compress&cs=tinysrgb&w=400',
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The Diwali collection was absolutely stunning! Every piece radiated festive energy. My lehenga was the highlight of every gathering.',
            name: 'Ananya Sharma',
            city: 'Jaipur',
            rating: 5,
        },
        {
            text: 'TARANG truly understands Indian celebrations. The Navratri outfit was vibrant, comfortable, and perfect for nine nights of dancing!',
            name: 'Meera Patel',
            city: 'Ahmedabad',
            rating: 5,
        },
        {
            text: 'Ordered a bridal lehenga and it exceeded all expectations. The craftsmanship and attention to detail is extraordinary. A festive dream!',
            name: 'Ritu Agarwal',
            city: 'Delhi',
            rating: 5,
        },
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div style={{ fontFamily: "'Karla', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen bg-white">
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Josefin+Sans:wght@400;500;600;700&family=Karla:wght@300;400;500;700&display=swap');

                @keyframes scrollPulse12 {
                    0%, 100% { opacity: 0.3; transform: translateY(0); }
                    50% { opacity: 1; transform: translateY(12px); }
                }
                .tarang-product-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px;
                }
                @media (min-width: 768px) {
                    .tarang-product-grid {
                        grid-template-columns: repeat(3, 1fr);
                        gap: 20px;
                    }
                }
                @media (min-width: 1024px) {
                    .tarang-product-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 24px;
                    }
                }
                .tarang-collection-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px;
                }
                @media (min-width: 768px) {
                    .tarang-collection-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 24px;
                    }
                }
                .tarang-instagram-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 4px;
                }
                @media (min-width: 768px) {
                    .tarang-instagram-grid {
                        grid-template-columns: repeat(6, 1fr);
                        gap: 4px;
                    }
                }
                .tarang-countdown-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 12px;
                }
                @media (min-width: 768px) {
                    .tarang-countdown-grid {
                        gap: 24px;
                    }
                }
            `}</style>

            {/* ===== SECTION 1: VIDEO HERO WITH SAFFRON OVERLAY ===== */}
            <section className="relative min-h-screen w-full overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#1A1A1A' }}>
                {/* Video Background */}
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=1200"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ opacity: 0.7 }}
                >
                    <source src="https://videos.pexels.com/video-files/3741028/3741028-uhd_2560_1440_30fps.mp4" type="video/mp4" />
                </video>

                {/* Saffron Gradient Overlay */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(135deg, rgba(234,88,12,0.6) 0%, rgba(194,65,12,0.4) 40%, rgba(0,0,0,0.5) 70%, rgba(234,88,12,0.3) 100%)'
                    }}
                />

                {/* Hero Content */}
                <div className="relative z-10 text-center px-4 sm:px-6">
                    <p
                        className="text-xs sm:text-sm tracking-[0.4em] uppercase mb-4 sm:mb-6"
                        style={{ color: '#FFF7ED', fontFamily: "'Karla', sans-serif", fontWeight: 500 }}
                    >
                        Festival Season 2026
                    </p>
                    <h1
                        className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold mb-4 sm:mb-6"
                        style={{
                            fontFamily: "'Josefin Sans', sans-serif",
                            color: '#FFFFFF',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                        }}
                    >
                        Celebrate<br />in Style
                    </h1>
                    <p
                        className="text-base sm:text-lg lg:text-xl font-light mb-8 sm:mb-10 max-w-lg mx-auto"
                        style={{
                            color: '#FFF7ED',
                            fontFamily: "'Karla', sans-serif",
                        }}
                    >
                        Vibrant festive fashion for every Indian celebration
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <button
                            onClick={() => navigate('/template12/shop')}
                            className="inline-flex items-center gap-3 px-8 py-3.5 sm:px-10 sm:py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: '#EA580C',
                                border: '2px solid #EA580C',
                                color: '#FFFFFF',
                                fontFamily: "'Josefin Sans', sans-serif",
                                fontWeight: 600,
                                borderRadius: '4px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#C2410C';
                                e.currentTarget.style.borderColor = '#C2410C';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#EA580C';
                                e.currentTarget.style.borderColor = '#EA580C';
                            }}
                        >
                            Shop Festive <ArrowRight className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => navigate('/template12/about')}
                            className="inline-flex items-center gap-3 px-8 py-3.5 sm:px-10 sm:py-4 text-sm tracking-[0.15em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: 'transparent',
                                border: '2px solid #FFFFFF',
                                color: '#FFFFFF',
                                fontFamily: "'Josefin Sans', sans-serif",
                                fontWeight: 600,
                                borderRadius: '4px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#FFFFFF';
                                e.currentTarget.style.color = '#EA580C';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                        >
                            Our Story
                        </button>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                    <div className="w-px h-12 opacity-40" style={{ backgroundColor: '#FFFFFF' }}>
                        <div
                            className="w-full h-1/2"
                            style={{
                                backgroundColor: '#FFFFFF',
                                animation: 'scrollPulse12 2s ease-in-out infinite'
                            }}
                        />
                    </div>
                </div>
            </section>

            {/* ===== SECTION 2: FESTIVAL COLLECTIONS ===== */}
            <section style={{ backgroundColor: '#FFFFFF' }} className="py-16 sm:py-24">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                    <div className="text-center mb-12">
                        <p
                            className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-3"
                            style={{ color: '#EA580C', fontFamily: "'Karla', sans-serif", fontWeight: 500 }}
                        >
                            Curated for Every Occasion
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
                            style={{ fontFamily: "'Josefin Sans', sans-serif", color: '#1A1A1A' }}
                        >
                            Festival Collections
                        </h2>
                        <div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#EA580C' }} />
                    </div>

                    <div className="tarang-collection-grid">
                        {collections.map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template12/shop')}
                                onMouseEnter={() => setHoveredCollection(index)}
                                onMouseLeave={() => setHoveredCollection(null)}
                                className="relative overflow-hidden cursor-pointer"
                                style={{
                                    borderRadius: '8px',
                                    borderBottom: `4px solid ${collection.color}`,
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    transform: hoveredCollection === index ? 'translateY(-4px)' : 'translateY(0)',
                                    boxShadow: hoveredCollection === index ? '0 12px 32px rgba(234,88,12,0.15)' : '0 2px 8px rgba(0,0,0,0.08)',
                                }}
                            >
                                <div style={{ aspectRatio: '3 / 4', overflow: 'hidden' }}>
                                    <img
                                        src={collection.image}
                                        alt={collection.name}
                                        className="w-full h-full object-cover"
                                        style={{
                                            transform: hoveredCollection === index ? 'scale(1.08)' : 'scale(1)',
                                            transition: 'transform 0.5s ease',
                                        }}
                                    />
                                    <div
                                        className="absolute inset-0"
                                        style={{
                                            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
                                        }}
                                    />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
                                    <h3
                                        className="text-lg sm:text-xl font-bold mb-1 text-white"
                                        style={{ fontFamily: "'Josefin Sans', sans-serif" }}
                                    >
                                        {collection.name}
                                    </h3>
                                    <p
                                        className="text-xs sm:text-sm text-white/80 leading-relaxed"
                                        style={{ fontFamily: "'Karla', sans-serif" }}
                                    >
                                        {collection.description}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: PRODUCTS GRID ===== */}
            <section style={{ backgroundColor: '#FFF7ED' }} className="py-16 sm:py-24">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                    <div className="text-center mb-12">
                        <p
                            className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-3"
                            style={{ color: '#EA580C', fontFamily: "'Karla', sans-serif", fontWeight: 500 }}
                        >
                            Handpicked for You
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
                            style={{ fontFamily: "'Josefin Sans', sans-serif", color: '#1A1A1A' }}
                        >
                            Festive Bestsellers
                        </h2>
                        <div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#EA580C' }} />
                    </div>

                    <div className="tarang-product-grid">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate(`/template12/product/${product.id}`)}
                                onMouseEnter={() => setHoveredProduct(product.id)}
                                onMouseLeave={() => setHoveredProduct(null)}
                                style={{
                                    backgroundColor: '#FFFFFF',
                                    borderRadius: '8px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    transform: hoveredProduct === product.id ? 'translateY(-4px)' : 'translateY(0)',
                                    boxShadow: hoveredProduct === product.id ? '0 12px 32px rgba(234,88,12,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
                                    borderBottom: hoveredProduct === product.id ? '3px solid #EA580C' : '3px solid transparent',
                                }}
                            >
                                <div style={{ position: 'relative', aspectRatio: '3 / 4', overflow: 'hidden' }}>
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover"
                                        style={{
                                            transform: hoveredProduct === product.id ? 'scale(1.05)' : 'scale(1)',
                                            transition: 'transform 0.5s ease',
                                        }}
                                    />
                                    {product.badge && (
                                        <span
                                            className="absolute top-3 left-3 px-3 py-1 text-[10px] sm:text-xs font-bold uppercase tracking-wider"
                                            style={{
                                                backgroundColor: '#EA580C',
                                                color: '#FFFFFF',
                                                borderRadius: '4px',
                                                fontFamily: "'Josefin Sans', sans-serif",
                                            }}
                                        >
                                            {product.badge}
                                        </span>
                                    )}
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center"
                                        style={{
                                            backgroundColor: 'rgba(255,255,255,0.9)',
                                            borderRadius: '50%',
                                            border: 'none',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease',
                                        }}
                                    >
                                        <Heart
                                            size={16}
                                            style={{
                                                color: wishlist[product.id] ? '#EA580C' : '#6B7280',
                                                fill: wishlist[product.id] ? '#EA580C' : 'none',
                                                transition: 'color 0.3s ease',
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-3 sm:p-4">
                                    <h3
                                        className="text-sm sm:text-base font-medium mb-1"
                                        style={{
                                            fontFamily: "'Karla', sans-serif",
                                            color: '#1A1A1A',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {product.name}
                                    </h3>
                                    <span
                                        className="text-base sm:text-lg font-bold"
                                        style={{ color: '#EA580C', fontFamily: "'Josefin Sans', sans-serif" }}
                                    >
                                        {product.price}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center mt-10">
                        <button
                            onClick={() => navigate('/template12/shop')}
                            className="inline-flex items-center gap-2 px-8 py-3.5 text-sm tracking-[0.15em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: '#EA580C',
                                color: '#FFFFFF',
                                border: '2px solid #EA580C',
                                fontFamily: "'Josefin Sans', sans-serif",
                                fontWeight: 600,
                                borderRadius: '4px',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#EA580C';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#EA580C';
                                e.currentTarget.style.color = '#FFFFFF';
                            }}
                        >
                            View All Products <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: FESTIVAL COUNTDOWN ===== */}
            <section
                className="py-16 sm:py-24 relative overflow-hidden"
                style={{
                    background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 50%, #EA580C 100%)',
                }}
            >
                <div className="max-w-[800px] mx-auto px-4 sm:px-6 text-center relative z-10">
                    <p
                        className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-3"
                        style={{ color: 'rgba(255,255,255,0.8)', fontFamily: "'Karla', sans-serif", fontWeight: 500 }}
                    >
                        <Clock className="inline w-4 h-4 mr-2 -mt-0.5" />
                        Countdown to Next Festival
                    </p>
                    <h2
                        className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 text-white"
                        style={{ fontFamily: "'Josefin Sans', sans-serif" }}
                    >
                        Get Festival Ready
                    </h2>
                    <div className="tarang-countdown-grid max-w-md mx-auto mb-10">
                        {[
                            { label: 'Days', value: countdown.days },
                            { label: 'Hours', value: countdown.hours },
                            { label: 'Minutes', value: countdown.minutes },
                            { label: 'Seconds', value: countdown.seconds },
                        ].map((item) => (
                            <div
                                key={item.label}
                                className="py-4 sm:py-6 px-2 rounded-lg"
                                style={{ backgroundColor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}
                            >
                                <div
                                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-1"
                                    style={{ fontFamily: "'Josefin Sans', sans-serif" }}
                                >
                                    {String(item.value).padStart(2, '0')}
                                </div>
                                <div
                                    className="text-[10px] sm:text-xs uppercase tracking-wider"
                                    style={{ color: 'rgba(255,255,255,0.7)', fontFamily: "'Karla', sans-serif" }}
                                >
                                    {item.label}
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template12/shop')}
                        className="inline-flex items-center gap-2 px-8 py-3.5 text-sm tracking-[0.15em] uppercase transition-all duration-300"
                        style={{
                            backgroundColor: '#FFFFFF',
                            color: '#EA580C',
                            border: '2px solid #FFFFFF',
                            fontFamily: "'Josefin Sans', sans-serif",
                            fontWeight: 600,
                            borderRadius: '4px',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#FFFFFF';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#FFFFFF';
                            e.currentTarget.style.color = '#EA580C';
                        }}
                    >
                        Shop Now <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* ===== SECTION 5: TESTIMONIALS ===== */}
            <section style={{ backgroundColor: '#FFFFFF' }} className="py-16 sm:py-24">
                <div className="max-w-[800px] mx-auto px-4 sm:px-6 text-center">
                    <p
                        className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-3"
                        style={{ color: '#EA580C', fontFamily: "'Karla', sans-serif", fontWeight: 500 }}
                    >
                        Happy Customers
                    </p>
                    <h2
                        className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-12"
                        style={{ fontFamily: "'Josefin Sans', sans-serif", color: '#1A1A1A' }}
                    >
                        Festival Stories
                    </h2>

                    <div
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                        className="relative"
                    >
                        <div className="min-h-[200px] flex flex-col items-center justify-center">
                            <div className="flex gap-1 mb-6">
                                {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                                    <Star key={i} size={18} style={{ color: '#EA580C', fill: '#EA580C' }} />
                                ))}
                            </div>
                            <p
                                className="text-base sm:text-lg lg:text-xl font-light leading-relaxed mb-6 max-w-2xl"
                                style={{
                                    fontFamily: "'Karla', sans-serif",
                                    color: '#1A1A1A',
                                    fontStyle: 'italic',
                                }}
                            >
                                "{testimonials[activeTestimonial].text}"
                            </p>
                            <p
                                className="text-sm font-bold"
                                style={{ color: '#EA580C', fontFamily: "'Josefin Sans', sans-serif" }}
                            >
                                {testimonials[activeTestimonial].name}
                            </p>
                            <p
                                className="text-xs mt-1"
                                style={{ color: '#6B7280', fontFamily: "'Karla', sans-serif" }}
                            >
                                {testimonials[activeTestimonial].city}
                            </p>
                        </div>

                        <div className="flex justify-center gap-2 mt-8">
                            {testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setActiveTestimonial(index)}
                                    className="transition-all duration-300"
                                    style={{
                                        width: activeTestimonial === index ? '24px' : '8px',
                                        height: '8px',
                                        borderRadius: '4px',
                                        backgroundColor: activeTestimonial === index ? '#EA580C' : '#E5E7EB',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: INSTAGRAM GRID ===== */}
            <section style={{ backgroundColor: '#FFF7ED' }} className="py-16 sm:py-24">
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                    <div className="text-center mb-10">
                        <p
                            className="text-xs sm:text-sm tracking-[0.3em] uppercase mb-3"
                            style={{ color: '#EA580C', fontFamily: "'Karla', sans-serif", fontWeight: 500 }}
                        >
                            <Instagram className="inline w-4 h-4 mr-2 -mt-0.5" />
                            Follow Our Journey
                        </p>
                        <h2
                            className="text-3xl sm:text-4xl lg:text-5xl font-bold"
                            style={{ fontFamily: "'Josefin Sans', sans-serif", color: '#1A1A1A' }}
                        >
                            @tarangfashion
                        </h2>
                    </div>

                    <div className="tarang-instagram-grid">
                        {instagramImages.map((img, index) => (
                            <div
                                key={index}
                                className="relative overflow-hidden cursor-pointer"
                                style={{ aspectRatio: '1 / 1' }}
                            >
                                <img
                                    src={img}
                                    alt={`Instagram ${index + 1}`}
                                    className="w-full h-full object-cover transition-transform duration-500"
                                    style={{ transform: 'scale(1)' }}
                                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                />
                                <div
                                    className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300"
                                    style={{ backgroundColor: 'rgba(234,88,12,0.5)' }}
                                    onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.opacity = '0'; }}
                                >
                                    <Instagram size={24} style={{ color: '#FFFFFF' }} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: NEWSLETTER ON SAFFRON BG ===== */}
            <section
                className="py-16 sm:py-24"
                style={{ background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)' }}
            >
                <div className="max-w-[600px] mx-auto px-4 sm:px-6 text-center">
                    <h2
                        className="text-3xl sm:text-4xl font-bold mb-3 text-white"
                        style={{ fontFamily: "'Josefin Sans', sans-serif" }}
                    >
                        Join the Celebration
                    </h2>
                    <p
                        className="text-sm sm:text-base mb-8 text-white/80"
                        style={{ fontFamily: "'Karla', sans-serif" }}
                    >
                        Be the first to know about festive launches, exclusive offers, and early access to new collections.
                    </p>
                    <form
                        onSubmit={(e) => { e.preventDefault(); if (email.trim()) setEmail(''); }}
                        className="flex flex-col sm:flex-row gap-3 sm:gap-0"
                    >
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email address"
                            required
                            className="flex-1 px-5 py-3.5 text-sm outline-none"
                            style={{
                                backgroundColor: 'rgba(255,255,255,0.95)',
                                border: 'none',
                                color: '#1A1A1A',
                                fontFamily: "'Karla', sans-serif",
                                borderRadius: '4px 0 0 4px',
                            }}
                        />
                        <button
                            type="submit"
                            className="px-8 py-3.5 text-sm uppercase tracking-wider transition-all duration-300"
                            style={{
                                backgroundColor: '#1A1A1A',
                                color: '#FFFFFF',
                                border: 'none',
                                fontFamily: "'Josefin Sans', sans-serif",
                                fontWeight: 600,
                                borderRadius: '0 4px 4px 0',
                                cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#000000'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#1A1A1A'; }}
                        >
                            Subscribe
                        </button>
                    </form>
                </div>
            </section>
        </div>
    );
};

export default Template12;
